import { useAtomValue } from "jotai";
import { allPass, ascend, find, is, map, path, prop, propEq, sortWith } from "ramda";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useListData } from "react-stately";
import { Virtuoso } from "react-virtuoso";
import { titleCase } from "title-case";
import { match } from "ts-pattern";
import { HeaderBar, HeaderDivider, LoadingSpinner, Modal, VStack } from "widgets";
import { useOverlay } from "widgets/hooks";

import { matchFilter, parseFilterExpression } from "@/app-utils/filter.ts";
import { useError } from "@/app-utils/graphql/useError.ts";
import { useDocumentTitle } from "@/app-utils/hooks/useDocumentTitle.ts";
import { getSavedItemTypeLabel, pluralizeWord } from "@/app-utils/string.ts";
import type { SavedItemsListing } from "@/graphql.ts";
import { useGetProjectForPagesQuery } from "@/modules/projects/graphql/projects-queries.gql.ts";
import ItemListTabToolbar from "@/modules/saved-items/components/ItemListTabToolbar.tsx";
import { SavedItem } from "@/modules/saved-items/components/SavedItem";
import {
  type SavedItemFragment,
  useGetSavedItemsForListingLazyQuery,
} from "@/modules/saved-items/graphql/saved-items.gql";
import { savedItemHeaderInfoAtom } from "@/modules/saved-items/store/atoms.ts";
import { updateUserParams } from "@/redux/modules/auth.ts";
import type { IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { FEATURE_DISPLAY_NAME, type ISortInfo, SORT_OPTIONS, type SortingField } from "@/settings/settings.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { AsyncSearchWithExprParser } from "@/ui-modules/core/forms/AsyncSearchWithExprParser.tsx";
import { SelectAllNoneWidget, SelectItem, SelectWidget } from "@/ui-modules/core/forms/SelectWidget.tsx";
import DataContextIndicator from "@/ui-modules/core/grid/DataContextIndicator.tsx";
import { DeleteSavedItemDialog } from "@/ui-modules/entities/DeleteSavedItemDialog.tsx";
import { EditSavedItemDialog } from "@/ui-modules/entities/EditSavedItemDialog.tsx";

import style from "../styles/Item.module.scss";

interface SavedItemsViewProps {
  type: string;
  subType: string;
  projectId?: number;
  tagIds?: number[];
  keepProjectIcon?: boolean;
}

/**
 * This component uses savedItemHeaderInfoAtom to set `title` and `count` in the Header. It usually comes from the Navigation Panel.
 */
export const SavedItemsView: FC<SavedItemsViewProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const savedItemHeaderInfo = useAtomValue(savedItemHeaderInfoAtom);

  const projectQuery = useGetProjectForPagesQuery({
    variables: { projectId: props.projectId },
    skip: props.projectId == undefined,
  });
  const project = projectQuery.data?.project;

  void useError(projectQuery.error);

  const [getSavedItems, { data, loading, error }] = useGetSavedItemsForListingLazyQuery();

  void useError(error);

  const loadSavedItems = useCallback(() => {
    void getSavedItems({
      variables: {
        type: props.type,
        subType: props.subType,
        projectId: props.projectId ?? -1,
        tagIds: is(Array, props.tagIds) && props.tagIds.length > 0 ? props.tagIds : null,
      },
    });
  }, [props.type, props.subType, props.projectId, props.tagIds]);

  useEffect(() => {
    loadSavedItems();
  }, [loadSavedItems]);

  const [allSavedItems, setAllSavedItems] = useState<SavedItemFragment[]>([]);
  useEffect(() => {
    setAllSavedItems(data?.savedItems.items ?? []);
  }, [data]);

  const listing: SavedItemsListing = find(
    allPass([propEq(props.type, "type"), propEq(props.subType, "subType")]),
    data?.savedItems?.listing ?? [],
  );
  const title = pluralizeWord(savedItemHeaderInfo.title ?? FEATURE_DISPLAY_NAME[props.type] ?? " ");

  const list = useListData<SavedItemFragment>({
    filter(item, filterText) {
      return matchFilter(
        [
          item.name,
          item.description,
          item.viewLabel,
          item.creator?.firstName,
          item.creator?.lastName,
          item.creator?.username,
          item.status?.name,
          item.created,
          item.project?.name,
        ],
        parseFilterExpression(filterText),
      );
    },
  });

  useEffect(() => {
    // when we switch tabs the selection state needs to be cleared
    list.setSelectedKeys(new Set());
  }, [props.type, props.subType]);

  const sortOptions = useMemo<ISortInfo[]>(() => {
    const options = [SORT_OPTIONS.DATE_MODIFIED, SORT_OPTIONS.OWNER, SORT_OPTIONS.NAME];

    if (props.projectId == undefined) {
      options.push(SORT_OPTIONS.PROJECT);
    }

    return options;
  }, [props.projectId]);

  const sortOrder = useSelector<IAppState, string | null>(
    (state) =>
      (props.projectId != undefined
        ? state.auth.session?.params?.projectItemSortOrder
        : state.auth.session?.params?.homeItemSortOrder) ?? null,
  );

  useDocumentTitle(titleCase(`${title}${project ? ` - ${project.name}` : ""}`));

  useEffect(() => {
    // empty the list to replace the items ; but first keep the selection state so that it can be set again after the sorting
    const selectedKeys = list.selectedKeys;
    list.setSelectedKeys("all");
    list.removeSelectedItems();

    if (allSavedItems) {
      // in between some items query updates the list might be empty but we still go through this logic to keep the selected keys (e.g. filter by tag)
      const sorting = match<SortingField>(sortOrder as SortingField)
        // .with("modified", () => sortWith([descend(prop("modified"))])) // default
        .with("owner", () => sortWith([ascend(path(["creator", "username"])), ascend(path(["creator", "display"]))]))
        .with("name", () => sortWith([ascend(prop("name"))]))
        .with("project.name", () => sortWith([ascend(path(["project", "name"]))]))
        .otherwise(() => null);

      let items = allSavedItems;

      if (sorting != null) {
        items = sorting(items);
      }

      list.append(...items);
      list.setSelectedKeys(selectedKeys);
    }
  }, [allSavedItems, sortOrder]);

  const { overlayProps: editingDialogOverlayProps, state: editingDialogState } = useOverlay({});
  const { overlayProps: deleteDialogOverlayProps, state: deleteDialogState } = useOverlay({});

  const [actionableItem, setActionableItem] = useState<SavedItemFragment>();

  return (
    <VStack spacing={0} style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <HeaderBar
        title={titleCase(title)}
        tag={savedItemHeaderInfo.count != null ? { label: savedItemHeaderInfo.count } : undefined}
        style={{ boxShadow: "inset 0 -1px var(--ui-border)" }}
        actions={[
          <AsyncSearchWithExprParser
            key={"track-selection-dialog-widget-3"}
            controlledValue={list.filterText}
            onSearchExprChange={async (value) => list.setFilterText(value)}
          />,
          <HeaderDivider key="divider-1" />,
          <SelectAllNoneWidget
            key={"item-list-tab-toolbar-widget-1"}
            current={list.selectedKeys === "all" ? list.items.length : list.selectedKeys.size}
            max={list.items.length}
            onSelectAll={(all) => {
              if (all) {
                list.setSelectedKeys("all");
              } else {
                list.setSelectedKeys(new Set([]));
              }
            }}
          />,
          <SelectWidget
            key={"item-list-tab-toolbar-widget-2"}
            label={"Sort by"}
            icon={"sort"}
            selectionMode={"single"}
            selectedKeys={[sortOrder]}
            onSelectionChange={(selection) => {
              if (selection instanceof Set && [...selection][0]) {
                const order = String([...selection][0]);
                const userParams =
                  props.projectId != undefined ? { projectItemSortOrder: order } : { homeItemSortOrder: order };
                void dispatch(updateUserParams(userParams));
              }
            }}
          >
            {map(
              (option) => (
                <SelectItem key={option.field}>{option.label}</SelectItem>
              ),
              sortOptions,
            )}
          </SelectWidget>,
          <HeaderDivider key="divider-2" />,
          listing ? (
            <ItemListTabToolbar
              projectId={props.projectId}
              items={list.items}
              selectedIds={
                list.selectedKeys === "all"
                  ? list.items.map((item) => item.id!)
                  : (Array.from(list.selectedKeys).map((key) => Number(key.toString())) ?? [])
              }
              itemsData={listing}
              onRefreshNeeded={() => {
                list.setSelectedKeys(new Set()); // reset the selection (e.g. important after deletion)
                loadSavedItems();
              }}
            />
          ) : null,
        ]}
      />
      <div style={{ background: "var(--page-background)", height: "100%", position: "relative" }}>
        {loading ? (
          <LoadingSpinner inCenter />
        ) : (
          <>
            {list.items.length == 0 ? (
              <ContentPlaceholder>There are no {title} to display, please refine your filters.</ContentPlaceholder>
            ) : null}
            <Virtuoso
              style={{ height: "100%" }}
              data={list.items}
              className={style.virtual}
              increaseViewportBy={{ top: 100, bottom: 100 }}
              itemContent={(_index, item) => {
                return (
                  /* adding margin as a list gap to the saveItem component would break the virtuoso height calculation, we should only use padding and therefore use a wrapper element when used in the context of a list */
                  <div className={style.virtual_list_element}>
                    <SavedItem
                      key={String(item.id)}
                      item={item}
                      isSelected={list.selectedKeys === "all" || list.selectedKeys.has(String(item.id))}
                      showProjectLabels={!props.projectId || !!props.keepProjectIcon}
                      onEditItem={() => {
                        setActionableItem(item);
                        editingDialogState.open();
                      }}
                      onDeleteItem={() => {
                        setActionableItem(item);
                        deleteDialogState.open();
                      }}
                      onItemSelectionChange={(_, selected) => {
                        const selectedKeys =
                          list.selectedKeys === "all"
                            ? new Set(map((i) => String(i.id), allSavedItems))
                            : list.selectedKeys;

                        if (selected) {
                          selectedKeys.add(String(item.id));
                        } else {
                          selectedKeys.delete(String(item.id));
                        }

                        list.setSelectedKeys(selectedKeys);
                      }}
                    />
                  </div>
                );
              }}
            />
            <DataContextIndicator
              numTotalRows={allSavedItems.length}
              numRowsFiltered={list.items.length}
              numRowsSelected={list.selectedKeys === "all" ? (allSavedItems.length ?? 0) : list.selectedKeys.size}
              rowEntityLabel={getSavedItemTypeLabel({ type: props.type, name: title })}
              onResetFilter={() => {
                list.setFilterText("");
              }}
              filterIsSet={list.filterText !== ""}
            />
          </>
        )}
      </div>

      <Modal state={editingDialogState} {...editingDialogOverlayProps}>
        <EditSavedItemDialog
          project={project}
          itemList={listing}
          savedItem={actionableItem!}
          onApply={(editedItem: SavedItemFragment) => {
            setAllSavedItems(
              allSavedItems.map((savedItem) => {
                return editedItem.id === savedItem.id ? editedItem : savedItem;
              }),
            );
          }}
          onClose={editingDialogState.close}
        />
      </Modal>

      <Modal state={deleteDialogState} {...deleteDialogOverlayProps}>
        <DeleteSavedItemDialog
          savedItem={actionableItem!}
          itemList={listing}
          onApply={(deletedItem: SavedItemFragment) => {
            setAllSavedItems(
              allSavedItems.filter((savedItem) => {
                return deletedItem.id !== savedItem.id;
              }),
            );
            list.remove(String(deletedItem.id));
          }}
          onClose={deleteDialogState.close}
        />
      </Modal>
    </VStack>
  );
};
